import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  DollarSign,
  User,
  Loader2,
  AlertCircle,
  Pencil,
  Trash2,
  Building2,
  Hash,
  UserCheck,
  Briefcase,
  FileText,
  ShieldCheck,
  History,
  CheckCircle2,
  XCircle,
  Download
} from 'lucide-react';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../../components/Common/Footer/Footer';
import styles from '../Overtime/ViewOTAttendance.module.css'; // Reusing premium view styles
import advanceStyles from './AdvanceRequests.module.css';
import AdvanceRequestDrawer from './AdvanceRequestDrawer';
import ApprovalModal from './ApprovalModal';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import toast from 'react-hot-toast';
import NotFound from '../../../pages/Error/NotFound';
import api from '../../../api/axios';

const MySwal = withReactContent(Swal);

const ViewAdvanceRequest = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(null);
  const [logs, setLogs] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);

  const fetchRequestDetails = async () => {
    setLoading(true);
    try {
      const [detailsRes, logsRes] = await Promise.all([
        api.get(`/advance-requests/${id}`),
        api.get(`/advance-requests/${id}/logs`)
      ]);
      
      if (detailsRes.data.success) {
        setRecord(detailsRes.data.data);
      }
      if (logsRes.data.success) {
        setLogs(logsRes.data.data);
      }
    } catch (error) {
      console.error('Error fetching details:', error);
      toast.error('Failed to load request details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequestDetails();
  }, [id]);

  const handleDelete = () => {
    MySwal.fire({
      title: 'Delete Request?',
      text: `Are you sure you want to remove this advance request for ${record?.employee_name || record?.employee?.name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/advance-requests/${id}`);
          toast.success('Request deleted');
          navigate('/employees/advance-requests');
        } catch (error) {
          toast.error('Failed to delete request');
        }
      }
    });
  };

  const getStatusClass = (status) => {
    if (!status) return '';
    switch (status.toLowerCase()) {
      case 'approved': return styles['status-approved'];
      case 'partially_approved': return styles['status-pending']; // Or custom
      case 'rejected': return styles['status-rejected'];
      default: return styles['status-pending'];
    }
  };

  if (loading) {
    return (
      <div className={styles['view-ot-layout']}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={() => setIsSidebarOpen(true)} />
          <div className={styles['loading-container']}>
            <Loader2 size={40} className={styles['spinner']} />
            <p>Fetching Advance Request details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!record) {
    return <NotFound />;
  }

  return (
    <div className={styles['view-ot-layout']}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs customItems={[
            { label: 'HR', path: '#' },
            { label: 'Advance Requests', path: '/employees/advance-requests' },
            { label: 'View Details', path: `/employees/advance-requests/view/${id}` }
          ]} />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Advance Request Details</h1>
              <p>Granular view of salary advance application and approval status.</p>
            </div>
            <div className={styles['header-actions']}>
              <button className={styles['btn-secondary']} onClick={() => navigate('/employees/advance-requests')}>
                <ArrowLeft size={18} />
                <span>Back</span>
              </button>
              <button className={styles['btn-primary']} onClick={() => setIsDrawerOpen(true)}>
                <Pencil size={18} />
                <span>Edit Request</span>
              </button>
              <button className={styles['btn-danger']} onClick={handleDelete}>
                <Trash2 size={18} />
              </button>
            </div>
          </div>

          <div className={styles['ot-hero-card']}>
            <div className={styles['hero-content']}>
              <div className={styles['hero-left']}>
                <div className={styles['employee-avatar']} style={{ background: '#fef3c7', color: '#d97706', border: '1px solid #fde68a' }}>
                  {(record.employee_name || record.employee?.name || 'E').charAt(0)}
                </div>
                <div className={styles['employee-info']}>
                  <div className={styles['name-status-row']}>
                    <h2>{record.employee_name || record.employee?.name}</h2>
                    <span className={`${styles['status-pill']} ${getStatusClass(record.status)}`}>
                      {record.status?.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  <div className={styles['meta-row']}>
                    <div className={styles['meta-item']}>
                      <Hash size={14} /> <span>{record.employee_code || record.employee?.employee_code || '---'}</span>
                    </div>
                    <div className={styles['meta-item']}>
                      <Building2 size={14} /> <span>{record.department_name || record.employee?.department?.name || '---'}</span>
                    </div>
                    <div className={styles['meta-item']}>
                      <Briefcase size={14} /> <span>{record.designation_name || record.employee?.designation?.name || '---'}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles['hero-right']}>
                 <div className={styles['site-location-box']} style={{ borderColor: '#fde68a', background: '#fffbeb' }}>
                    <label style={{ color: '#d97706' }}>Request Date</label>
                    <div className={styles['site-value']}>
                       <Calendar size={18} />
                       <span>{record.request_date || record.date}</span>
                    </div>
                 </div>
              </div>
            </div>
          </div>

          <div className={styles['metrics-grid']}>
             <div className={styles['metric-card']}>
                <div className={`${styles['metric-icon-box']} ${styles['blue']}`}><DollarSign size={20} /></div>
                <div className={styles['metric-info']}>
                   <label>Requested Amount</label>
                   <h3>₹{parseFloat(record.requested_amount || 0).toLocaleString('en-IN')}</h3>
                </div>
             </div>
             <div className={styles['metric-card']}>
                <div className={`${styles['metric-icon-box']} ${styles['green']}`}><ShieldCheck size={20} /></div>
                <div className={styles['metric-info']}>
                   <label>Approved Amount</label>
                   <h3>₹{parseFloat(record.approved_amount || 0).toLocaleString('en-IN')}</h3>
                </div>
             </div>
             <div className={styles['metric-card']}>
                <div className={`${styles['metric-icon-box']} ${styles['orange']}`}><UserCheck size={20} /></div>
                <div className={styles['metric-info']}>
                   <label>Approver</label>
                   <h3>{record.approver_name || record.approved_by || '-'}</h3>
                </div>
             </div>
             <div className={styles['metric-card']}>
                <div className={`${styles['metric-icon-box']} ${styles['red']}`}><FileText size={20} /></div>
                <div className={styles['metric-info']}>
                   <label>Documents</label>
                   <h3>{record.documents?.length || 0} Files</h3>
                </div>
             </div>
          </div>

          <div className={styles['details-grid']}>
             <div className={styles['main-column']}>
                <div className={styles['details-card']}>
                   <div className={styles['card-header']}>
                      <FileText size={20} />
                      <h3>Reason & Remarks</h3>
                   </div>
                   <div className={styles['reason-box']} style={{ borderLeftColor: '#f59e0b' }}>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px', fontStyle: 'normal' }}>Request Note</label>
                      "{record.request_remark || record.remark || 'No remarks provided.'}"
                   </div>
                   
                   {record.approved_remark && (
                     <div className={styles['reason-box']} style={{ borderLeftColor: '#10b981', marginTop: '16px' }}>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px', fontStyle: 'normal' }}>Approval Remark</label>
                        "{record.approved_remark}"
                     </div>
                   )}
                </div>
             </div>

             <div className={styles['side-column']}>
                <div className={styles['details-card']}>
                   <div className={styles['card-header']}>
                      <ShieldCheck size={20} />
                      <h3>Approval Audit</h3>
                   </div>
                   <div className={styles['compliance-list']}>
                      <div style={{ marginBottom: '16px' }}>
                         <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '4px' }}>Approved By</label>
                         <span style={{ fontWeight: 600, color: '#334155' }}>{record.approver_name || record.approved_by || 'Not Processed'}</span>
                      </div>
                      <div>
                         <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '4px' }}>Approval Time</label>
                         <span style={{ fontWeight: 600, color: '#334155' }}>{record.approval_date || record.approved_at || '-'}</span>
                      </div>
                   </div>
                </div>
             </div>
          </div>

          <Footer />
        </main>
      </div>

      <AdvanceRequestDrawer 
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSave={() => {
           setIsDrawerOpen(false);
           fetchRequestDetails();
        }}
        record={record}
      />

      <ApprovalModal 
        isOpen={isApprovalModalOpen}
        onClose={() => setIsApprovalModalOpen(false)}
        record={record}
        onSuccess={() => fetchRequestDetails()}
      />
    </div>
  );
};

export default ViewAdvanceRequest;
